import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Button } from '@/components/Button/Button';
import { OrgAdminRoleType } from '@/const/user';
import { FormikPanelRadioGroup } from '@/components/form/fields/FormikPanelRadioGroup/FormikPanelRadioGroup';
import { SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS } from '@/const/formConfigs';
import { EditAdminRoleModal } from '@/bundle/shared/components/modals/EditAdminRoleModal';
import { getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { editInternalOrganizationUserRole } from '@/bundle/_MasterAdmin/_OrganizationUserDetailsPage/api/api';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { useParams } from 'react-router';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';

type EditUserRoleFormType = {
  role: OrgAdminRoleType;
  onClose: () => void;
  onSuccess: () => void;
};

export const EditAdminRoleForm = ({ role, onClose, onSuccess }: EditUserRoleFormType) => {
  const { id } = useParams<{ id: string }>();

  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);

  const formik = useFormik({
    initialValues: { role },
    onSubmit: (values) => {
      const payload = getChangedFormValues(values, formik.initialValues);

      if (!payload) {
        onClose();

        return;
      }

      setIsEditRoleModalOpen(true);
    },
  });

  const { data, mutate, isPending } = useMutation({
    mutationKey: ['edit_internal_organization_user_role'],
    mutationFn: () => {
      return editInternalOrganizationUserRole(id, formik.values);
    },
    onSuccess(response) {
      if (response?.error) return;

      onSuccess();
      showSnackbar('The role has been changed.');
      onClose();
    },
    onSettled() {
      setIsEditRoleModalOpen(false);
    },
  });

  const userRole = formik.values.role;
  const formError = getResponseError(data?.error);

  return (
    <>
      <FormikForm value={formik} error={formError}>
        <FormikPanelRadioGroup
          name='role'
          label='Role*'
          options={SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS}
          apiError={data?.error}
        />
        <ButtonGroup marginTop='14px'>
          <Button variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit'>Save</Button>
        </ButtonGroup>
      </FormikForm>

      <EditAdminRoleModal
        role={userRole}
        isOpen={isEditRoleModalOpen}
        onConfirm={mutate}
        onClose={() => setIsEditRoleModalOpen(false)}
        isLoading={isPending}
      />
    </>
  );
};
